/**
 * Copyright 2022-present terrestris GmbH & Co. KG
 *
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 *
 * 1. Redistributions of source code must retain the above copyright notice, this
 * list of conditions and the following disclaimer.
 *
 * 2. Redistributions in binary form must reproduce the above copyright notice,
 * this list of conditions and the following disclaimer in the documentation
 * and/or other materials provided with the distribution.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 * IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE
 * OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 */
import React, { useMemo } from 'react';

import {
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import {
  FontAwesomeIcon
} from '@fortawesome/react-fontawesome';

import {
  useTranslation
} from 'react-i18next';

import {
  SearchField
} from '@terrestris/react-geo';

import {
  createNominatimGetExtentFunction,
  createNominatimGetValueFunction,
  createNominatimSearchFunction,
  useMap
} from '@terrestris/react-util';

import {
  useAppSelector
} from '../../hooks/useAppSelector';

import './BasicNominatimSearch.less';

export const BasicNominatimSearch: React.FC = (): React.JSX.Element => {
  const map = useMap();
  const {
    t
  } = useTranslation();

  const nominatimSearchFunction = useMemo(() => createNominatimSearchFunction({
    bounded: 1,
    viewBox: '8.0577353, 52.7993012, 8.6847415, 53.1686406',
    countryCodes: 'de',
    searchResultLanguage: 'de',
    nominatimBaseUrl: 'https://nominatim.terrestris.de/search.php?'
  }), []);
  const nominatimGetValue = useMemo(() => createNominatimGetValueFunction(), []);
  const nominatimGetExtent = useMemo(() => createNominatimGetExtentFunction(), []);

  const featureInfoDrawerVisible = useAppSelector((state) => state.featureInfo.active);
  const sideDrawerVisible = useAppSelector((state) => state.drawer.visible);

  if (!map) {
    return <></>;
  }

  return (
    <SearchField
      searchFunction={nominatimSearchFunction}
      className={`rg-searchfield ${featureInfoDrawerVisible || sideDrawerVisible ? 'moved' : ''}`}
      getValue={nominatimGetValue}
      getExtent={nominatimGetExtent}
      allowClear
      showSearch
      placeholder={
        <div>
          <FontAwesomeIcon
            icon={faSearch}
            size='lg'
          />
          { t('Nominatim.placeholder') }
        </div>
      }
    />
  );
};

export default BasicNominatimSearch;
